import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiVersionsArticleByIdQuery,
  usePutApiVersionsByIdMutation,
} from '../../../redux/store/api/api';
import {
  selectContextVersion,
  selectRenameVersionDialogOpened,
  setRenameVersionDialogOpened,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import { EDIT_MODE_KEY } from '../../../shared/constants';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';

function RenameVersionDialog(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t: translation } = useTranslation();
  const dialogShow = useAppSelector(selectRenameVersionDialogOpened);
  const version = useAppSelector(selectContextVersion);
  const [versionName, setVersionName] = useState(version.name || '');
  const [versionNameIsValid, setVersionNameIsValid] = useState(true);
  const [updateVersion, { isError, isLoading, error }] =
    usePutApiVersionsByIdMutation();
  const [editModeIsActive] = useLocalStorage<boolean>(EDIT_MODE_KEY, false);
  const { refetch } = useGetApiVersionsArticleByIdQuery(
    version.articleId
      ? { id: version.articleId, editMode: editModeIsActive }
      : skipToken,
  );

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'UpdateVersionError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    if (dialogShow) {
      setVersionName(version.name || '');
      setVersionNameIsValid(true);
    }
  }, [dialogShow]);

  const handleUpdateVersion = () => {
    if (versionName.trim().length === 0) {
      setVersionNameIsValid(false);
      return;
    }

    updateVersion({
      id: version.id || '',
      namedVersion: {
        id: version.id,
        name: versionName.trim(),
        title: version.title,
        articleId: version.articleId,
        publishedFrom: version.publishedFrom,
        publishedUntil: version.publishedUntil,
        type: version.type,
        htmlContent: version.htmlContent,
        validFrom: version.validFrom,
        disabled: version.disabled,
        draft: version.draft,
      },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'UpdateVersionSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        dispatch(setRenameVersionDialogOpened(false));
        refetch();
      });
  };

  return (
    <CustomDialog
      titleId='RenameVersionDialog'
      show={dialogShow}
      closeFunction={() => {
        dispatch(setRenameVersionDialogOpened(false));
      }}
      closeTitle={translation('cancel')}
      actionFunction={() => {
        handleUpdateVersion();
      }}
      actionTitle={translation('save')}
      actionButtonDisabled={version.name === versionName.trim() || isLoading}
      dialogTitle={translation('renameVersion')}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <p>{translation('fieldsAreRequiredLegend')}</p>
          <Form.Group className='mb-3' controlId='VersionName'>
            <Form.Label>{translation('name')}*</Form.Label>
            <Form.Control
              aria-describedby={
                versionNameIsValid ? undefined : 'VersionNameInputError'
              }
              isInvalid={!versionNameIsValid}
              required
              onChange={(e) => {
                setVersionName(e.target.value);
                if (e.target.value.length > 0) {
                  setVersionNameIsValid(true);
                }
              }}
              type='text'
              value={versionName}
            />
            <Form.Control.Feedback id='VersionNameInputError' type='invalid'>
              {translation('fieldNotEmpty')}
            </Form.Control.Feedback>
          </Form.Group>
        </>
      )}
    </CustomDialog>
  );
}

export default RenameVersionDialog;
